import React, { useState } from 'react'
import { Tooltip2 } from '@blueprintjs/popover2'
import { seatable, primarySeat } from '@ui/canvas/utils'
import get from 'lodash/get'
import { Button } from '@runeffective/evo-design-system'
import api, { ICurrentUser, ISpace, IFloorMapSpace } from '@api'
import SwitchSpacesOptimistically from '@utils/SwitchSpacesOptimistically'
import { useFloor } from '@providers/mapProvider'
import { Mode } from '@ui/selects/LocationSelect'
import useLocations from '@ui/selects/LocationSelect/useLocations'
import useSelection from '@ui/selects/LocationSelect/useSelection'
import useSettings from 'components/NewSettings/hooks/useSettings'

interface SelfMoveButtonProps {
  user: ICurrentUser | null
  space: ISpace
  selfmoveOn?: boolean
  reload?: () => void
}

const SelfMoveButton = (props: SelfMoveButtonProps) => {
  const [state, setState] = useState({ isFetching: false })

  const { user, space, selfmoveOn } = props
  const { isFetching } = state
  const floor = useFloor()

  const regions = useLocations()
  const floorId = floor?.floorId as string
  const spaces = floor.fetchSpaces?.()
  const value = useSelection(Mode.SINGLE_FLOOR, floorId, regions)
  const selection = floor.selection
  const byId = spaces?.['byId']
  const setting = useSettings()

  const onClick = () => {
    const { space } = props
    setState({ isFetching: true })

    api.employee.selfMove(space.id).then(() => {
      setState({ isFetching: false })
      props.reload?.()
      const updatedSpaces = SwitchSpacesOptimistically({
        setting: setting,
        user: user,
        byId: byId,
        selection: selection,
      })
      floor.updateSpaces?.(updatedSpaces as unknown as IFloorMapSpace[])
    })
  }

  if (
    !selfmoveOn ||
    user?.neighborhoods?.length ||
    !seatable(space.spaceType) ||
    !primarySeat(space.usageType) ||
    space.spaceAssignment.neighborhood ||
    space.id === get(user, 'space.id')
  ) {
    return null
  }

  const employee = get(space, 'spaceAssignment.employee')

  if (employee) {
    const name = employee.name.split(/\s/)[0]
    const button = (
      <Button
        style={{ height: 40 }}
        size="small"
        disabled={isFetching || !user?.space}
        onClick={() => {
          if (user?.space) {
            onClick()
          }
        }}
        id="switch_seats">
        {isFetching ? 'Processing...' : `Switch seats with ${name}`}
      </Button>
    )

    return user?.space ? (
      button
    ) : (
      <Tooltip2 content={`You must have an assigned seat to switch with ${name}`} position="top">
        <div style={{ cursor: 'not-allowed' }}>{button}</div>
      </Tooltip2>
    )
  }

  return (
    <Button size="small" disabled={isFetching} onClick={onClick} id="move_here">
      {isFetching ? 'Processing...' : 'Move here'}
    </Button>
  )
}

export default SelfMoveButton
